import type { StatusChipVariant } from '@/components/ui/status-chip';
import type { ConsoleUser, UserSegment, UserState, UserType } from '@/services/api/user-console';
import { sentenceCase } from '@/utils/string';

const STATE_TITLES: Record<UserState, string> = {
  ACTIVE: 'Active',
  PENDING: 'Pending',
  BLOCKED: 'Blocked',
  OFFBOARDED: 'Offboarded',
  DELETED: 'Closed',
};

export const USER_STATE_CHIP_VARIANTS: Record<UserState, StatusChipVariant> = {
  ACTIVE: 'success',
  PENDING: 'warning',
  BLOCKED: 'destructive',
  OFFBOARDED: 'destructive',
  DELETED: 'muted',
};

const SEGMENT_TITLES: Record<UserSegment, string> = {
  RETAIL: 'Retail',
  INSTITUTIONAL: 'Institutional',
  MARKET_MAKER: 'Market maker',
};

const USER_TYPE_TITLES: Record<UserType, string> = {
  ONE_TRADING_CLIENT: 'One Trading client',
  BROKER: 'Broker',
  BROKER_CLIENT: 'Broker client',
  BROKER_CLIENT_SUBACCOUNT: 'Broker client sub-account',
};

/** Unknown wire values fall back to a title-cased rendering rather than a blank. */
export const formatUserStateTitle = (state: string): string => STATE_TITLES[state as UserState] ?? sentenceCase(state);
export const formatSegmentTitle = (segment: string): string => SEGMENT_TITLES[segment as UserSegment] ?? sentenceCase(segment);
export const formatUserTypeTitle = (userType: string): string => USER_TYPE_TITLES[userType as UserType] ?? sentenceCase(userType);

export const userStateChipVariant = (state: string): StatusChipVariant => USER_STATE_CHIP_VARIANTS[state as UserState] ?? 'muted';

export interface UserHeading {
  title: string;
  /** Email under a name title; absent when the email is already the title */
  email?: string;
}

/** Name first (broker clients have no email), then email, then the most specific identifier */
export const userHeading = (user: Pick<ConsoleUser, 'name' | 'email' | 'FastId' | 'PID'>): UserHeading => {
  if (user.name) return { title: user.name, email: user.email };
  if (user.email) return { title: user.email };
  return { title: user.FastId ? `FAST ID ${user.FastId}` : user.PID };
};

/** Header shows exceptions only — an all-allowed account renders no restriction chips. */
export const restrictedFeatures = (user: Pick<ConsoleUser, 'spotEnabled' | 'futuresEnabled' | 'instantTradeEnabled'>): string[] =>
  [!user.spotEnabled && 'Spot', !user.futuresEnabled && 'Futures', !user.instantTradeEnabled && 'Instant trade'].filter((label): label is string => Boolean(label));
